"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatErrorLines = exports.formatError = void 0;
const effect_1 = require("effect");
const ANONYMOUS_ROOT = '<anonymous>';
// Default Effect titles — noise as field identifiers.
const NOISE_IDENTIFIERS = new Set([
    'string',
    'number',
    'boolean',
    'object',
    'any',
    'unknown',
    'never',
    'null',
    'undefined',
    'void',
    'symbol',
    'bigint',
]);
const getIdentifier = (ast) => effect_1.SchemaAST.getIdentifierAnnotation(ast).pipe(effect_1.Option.orElse(() => effect_1.SchemaAST.getTitleAnnotation(ast)), effect_1.Option.filter(id => !NOISE_IDENTIFIERS.has(id)));
const getRootIdentifier = (issue) => {
    switch (issue._tag) {
        case 'Pointer':
        case 'Unexpected':
            return effect_1.Option.none();
        default:
            return getIdentifier(issue.ast);
    }
};
// String keys read as `.name`; numeric/symbol keys read as `[0]` / `[Symbol(x)]`.
const formatPathSegment = (key) => typeof key === 'string' ? `.${key}` : `[${key.toString()}]`;
const formatPath = (path) => path.map(formatPathSegment).join('');
const truncate = (s) => (s.length > 120 ? `${s.slice(0, 120)}...` : s);
const formatExpected = (ast) => truncate(effect_1.Option.getOrElse(getIdentifier(ast), () => String(ast)));
const formatActual = (value) => {
    if (value === undefined)
        return 'undefined';
    if (typeof value === 'bigint')
        return `${value.toString()}n`;
    if (typeof value === 'symbol')
        return value.toString();
    if (typeof value === 'function')
        return '[Function]';
    try {
        return truncate(JSON.stringify(value) ?? String(value));
    }
    catch {
        return truncate(String(value));
    }
};
// Build the leaf message from issue fields directly — no dependence on
// TreeFormatter wording. Respects per-issue custom `message` when present.
const formatLeafMessage = (issue) => {
    if (issue.message !== undefined)
        return issue.message;
    switch (issue._tag) {
        case 'Type':
            return `expected ${formatExpected(issue.ast)}, got ${formatActual(issue.actual)}`;
        case 'Missing':
            return `is missing (expected ${formatExpected(issue.ast.type)})`;
        case 'Unexpected':
            return 'is unexpected';
        case 'Forbidden':
            return 'is forbidden';
    }
};
const formatLine = (issue) => {
    const root = `(${issue.rootIdentifier ?? ANONYMOUS_ROOT})`;
    const showField = issue.fieldIdentifier && issue.fieldIdentifier !== issue.rootIdentifier;
    const field = showField ? ` (${issue.fieldIdentifier})` : '';
    return `${root}${formatPath(issue.path)}${field}: ${issue.message}`;
};
// Pointer resets fieldId — we're entering a new field's schema.
const walk = (issue, rootId, path, fieldId) => {
    switch (issue._tag) {
        case 'Pointer':
            return walk(issue.issue, rootId, [...path, ...effect_1.Array.ensure(issue.path)], effect_1.Option.none());
        case 'Composite': {
            const id = effect_1.Option.orElse(getIdentifier(issue.ast), () => fieldId);
            return effect_1.Array.ensure(issue.issues).flatMap(sub => walk(sub, rootId, path, id));
        }
        case 'Refinement':
        case 'Transformation':
            return walk(issue.issue, rootId, path, effect_1.Option.orElse(getIdentifier(issue.ast), () => fieldId));
        case 'Type':
            return [
                {
                    rootIdentifier: effect_1.Option.getOrUndefined(rootId),
                    path,
                    fieldIdentifier: effect_1.Option.getOrUndefined(effect_1.Option.orElse(getIdentifier(issue.ast), () => fieldId)),
                    message: formatLeafMessage(issue),
                },
            ];
        case 'Missing':
        case 'Unexpected':
        case 'Forbidden':
            return [
                {
                    rootIdentifier: effect_1.Option.getOrUndefined(rootId),
                    path,
                    fieldIdentifier: effect_1.Option.getOrUndefined(fieldId),
                    message: formatLeafMessage(issue),
                },
            ];
        default: {
            // Compile error if Effect adds a new ParseIssue variant.
            const _exhaustive = issue;
            return _exhaustive;
        }
    }
};
const formatError = (error) => walk(error.issue, getRootIdentifier(error.issue), [], effect_1.Option.none());
exports.formatError = formatError;
const formatErrorLines = (error) => (0, exports.formatError)(error).map(formatLine);
exports.formatErrorLines = formatErrorLines;
//# sourceMappingURL=parse-error-formatter.js.map